import { Fragment, useEffect, useId, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { ZONA_HORARIA, esCamion, formatMoney, type Role } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { leerSinEfecto, tuvoEfecto } from '../lib/sinEfecto.js';
import { readSettings } from '../db/reader.js';
import { sesionAbierta } from '../lib/caja.js';
import { hayQueCuadrar } from '../lib/cuadrar.js';
import { cajasDeOtroDia, hoyDelNegocio } from '../lib/dia.js';
import { tablero } from '../lib/vencimientos.js';
import { leerPendientes, porFijo } from '../lib/gastosFijos.js';
import { factoresDe, hayEnLaTienda, pedidosCortos } from '../lib/apartados.js';
import { useArranque } from '../lib/arranque.js';
import { enPractica } from '../lib/practica.js';
import { horasDeAviso, useAvisosDelServidor } from '../lib/equipos.js';
import { SEGUIDOS_PARA_AVISAR, seguidosAlDueno } from '../lib/pinesDelDueno.js';
import { atajosPara, columnasPara, ladoDelGlobo, tinteDe } from '../lib/atajos.js';
import { bloquear, usePermisos, usePuede, useSesion } from '../lib/sesion.js';
import { marcarOrigen } from '../lib/transiciones.js';
import { EstadoSync } from '../ui/EstadoSync.js';
import { ConGlobo } from '../ui/Globo.js';
import { Idioma } from '../ui/Idioma.js';
import { Chip, Ico, type NombreIcono } from '../ui/Iconos.js';
import { useCajaDeOtro } from '../ui/RecibirCaja.js';
import { useEmpiezaElDia } from '../ui/LoDelDia.js';
import { PreciosBajoMargen, usePreciosBajoMargen } from '../ui/PreciosBajoMargen.js';
import { Arranque, Esperando } from './Arranque.js';
import { contarAcabandose } from './Reponer.js';
import { diasDesde, pagosEnCola } from './Transferencias.js';
import { useControl } from './Control.js';
import { localeActual, useT, type Clave } from '../i18n/index.js';
import { etiquetaRol } from '../i18n/dominio.js';
import { useHoy } from '../lib/hoy.js';

const CLAVE_SALTADO = 'abastece.arranque.saltado';
/**
 * A one-way latch: this shop has been ready to sell at least once.
 *
 * Without it the setup flow would come back with the first product added without a price, which
 * un-does one of its steps. That is not a shop that needs setting up again; it is Tuesday. Setup
 * is a thing that happens once, so once it has happened it stops being allowed to take over the
 * screen, and the ordinary signals — the big contextual button, the unpriced-product nudge — carry
 * it from there. (Closing the till un-did another, every evening, until the till stopped being a
 * step of it, 2026-10-08.)
 */
const CLAVE_LISTO = 'abastece.arranque.listo';

const leerBandera = (clave: string) => {
  try {
    return localStorage.getItem(clave) === '1';
  } catch {
    return false;
  }
};

const escribirBandera = (clave: string, v: boolean) => {
  try {
    if (v) localStorage.setItem(clave, '1');
    else localStorage.removeItem(clave);
  } catch {
    /* A device that refuses storage just gets asked again next time. Harmless. */
  }
};

type Destino = '/pos' | '/caja/empezar' | '/caja/terminar';

interface Accion {
  etiqueta: Clave;
  destino: Destino;
  icono: NombreIcono;
}

/**
 * La pantalla de inicio.
 *
 * It used to be thirteen modules of equal weight in build order, three statistics that all read
 * zero on day one, and a footer counting rows in the log. The one obvious button, "Vender",
 * dead-ended on "No hay caja abierta" — the app knew perfectly well the till was closed and
 * offered selling anyway.
 *
 * So the big button is now whatever the next real thing is. Till closed, it starts the day, or the
 * shift when one has already closed here today. Till open, it sells. A driver is no different since
 * the owner took «Reparto» out (2026-10-04): their own till, their orders a shortcut away. A route
 * left open from before is settled first, so on its till the big button is «Liquidar la ruta». The
 * screen can still be wrong about what you wanted, but it can no longer offer something it knows
 * will fail.
 *
 * Three roles, three shapes. A cashier has seven things she can do and sees seven; the four
 * owner-only sections that used to sit here and refuse her are gone from her device entirely.
 *
 * Under the big button are the shortcuts (`lib/atajos.ts`): the screens somebody opens every
 * day, one tap away instead of two or three through «Todo lo demás». Nine for the owner, a few
 * for a cashier or a driver. Between the two sits whatever ends what the big button started,
 * «Terminar mi turno». It comes and goes with the till, so it has a bar of its own, and the
 * shortcuts never move when it does.
 */
export function Inicio() {
  const t = useT();
  const sesion = useSesion();
  /** Today and the shop's day, drawn again as they turn (`useHoy`): the screen stays open overnight. */
  const hoyEs = useHoy();
  const negocio = hoyDelNegocio();
  const arranque = useArranque();
  const [saltado, setSaltado] = useState(() => leerBandera(CLAVE_SALTADO));
  /*
   * The practice shop is a shop already running, whatever its till is doing, and the latch is the
   * device's: set from practice, it told the real shop on the same phone that it had been ready
   * to sell, and the real shop's setup never took over its screen again, finished or not.
   */
  const [yaEstuvoListo, setYaEstuvoListo] = useState(() => enPractica() || leerBandera(CLAVE_LISTO));

  const puede = usePermisos();
  /** The ids each shortcut's bubble is named by, unique to this screen. */
  const globo = useId();
  const puedeConfigurar = usePuede('editar_productos');
  /** Whoever sets what the products sell for: the owner (2026-10-13). */
  const puedePrecios = usePuede('editar_precios');
  /**
   * Whoever sets the shop up: its name is a setting, so the owner. Products and prices are a
   * cashier's too since 2026-10-09, and a cashier walked into the setup would stop at its name.
   */
  const puedeArrancar = usePuede('configurar');
  /** Whoever records the shop's expenses: the owner. */
  const puedeGastos = usePuede('configurar');
  const puedeComprar = usePuede('recibir_compras');
  const puedeCaja = usePuede('abrir_caja');
  const puedePedidos = usePuede('gestionar_clientes');
  const puedeRecibir = usePuede('recibir_efectivo');
  const puedeLiquidar = usePuede('liquidar_ruta');
  const puedeBitacora = usePuede('ver_bitacora');
  const puedeAjustar = usePuede('ajustar_inventario');
  /**
   * The week's control, for the owner: till shortages past the amount set in «Configuración»,
   * and sales that went through without the owner's yes.
   */
  const control = useControl(7, usePuede('ver_reportes'));
  const verCostos = usePuede('ver_costos');
  /**
   * List prices a cost increase left under the margin floor, and the sheet that raises them: the
   * owner's, who sees the margins, not everybody who keeps the prices.
   */
  const preciosBajos = usePreciosBajoMargen(puedeConfigurar && verCostos);
  /** Whoever confirms the transfers the queue holds, which is where its alert goes. */
  const puedeConfirmar = usePuede('confirmar_transferencia');
  const [viendoPrecios, setViendoPrecios] = useState(false);
  const usuariosTodos = useLiveQuery(() => db.usuarios.toArray(), [], []);
  /** Somebody else's drawer here: the next thing is to take it over, so that is the big button. */
  const cajaDeOtro = useCajaDeOtro();
  const empiezaElDia = useEmpiezaElDia();
  const delServidor = useAvisosDelServidor({ equipos: usePuede('gestionar_usuarios'), rechazos: puedeBitacora });
  /**
   * The most wrong tries in a row at the owner's PIN on one device in the last day: typed into an
   * authorisation, or on an owner's own account at the pad or a confirmation, with the right PIN
   * nowhere between them (`pinesDelDueno.ts`). A slip is fingers; ten in a row is somebody trying.
   */
  const pinesDelDueno = useLiveQuery(
    async () => (puedeBitacora ? seguidosAlDueno(new Date(Date.now() - 24 * 3_600_000).toISOString()) : 0),
    [puedeBitacora],
    0,
  );

  const config = useLiveQuery(() => readSettings(), [], null);
  // After `config`: the amount set in «Configuración» is what makes a shortage worth saying.
  const faltantesCaja = (control?.faltantes ?? [])
    .filter((f) => f.monto >= (config?.faltanteAviso ?? 0))
    .sort((a, b) => b.monto - a.monto);
  /** The till open on this device: null for none, undefined while it is read. */
  const caja = useLiveQuery(async () => (await sesionAbierta()) ?? null, [], undefined);
  /**
   * The tills still open from a day before, with the device each is on: a corte somebody forgot
   * is found the morning after, while whoever had the till still remembers the day (`cajasDeOtroDia`).
   */
  const deOtroDia = useLiveQuery(
    async () => {
      const [abiertas, dispositivos] = await Promise.all([
        db.sesiones.where('estado').equals('abierta').toArray(),
        db.dispositivos.toArray(),
      ]);
      return cajasDeOtroDia(abiertas, negocio).map((x) => ({
        ...x,
        equipo: dispositivos.find((d) => d.id === x.sesion.dispositivoId)?.nombre ?? '',
      }));
    },
    [negocio],
    [],
  );
  /**
   * Route days still open from before the owner took «Reparto» out (2026-10-04), with the device
   * each one's till is on: that till closes only by settling the route, on that device
   * (`routes/LiquidarRuta.tsx`). None opens any more, so this is empty but for those.
   */
  const rutasAbiertas = useLiveQuery(
    async () => {
      const [abiertas, sesiones, dispositivos] = await Promise.all([
        db.jornadas.where('estado').equals('abierta').toArray(),
        db.sesiones.where('estado').equals('abierta').toArray(),
        db.dispositivos.toArray(),
      ]);
      return abiertas.map((jornada) => {
        const dispositivoId = sesiones.find((s) => s.id === jornada.sesionId)?.dispositivoId;
        return { jornada, equipo: dispositivos.find((d) => d.id === dispositivoId)?.nombre ?? '' };
      });
    },
    [],
    [],
  );
  /** The transfer queue, sales' and top-ups' alike, and how many days the oldest has waited. */
  const cola = useLiveQuery(
    async () => {
      const todos = await pagosEnCola();
      return { n: todos.length, dias: todos[0] ? diasDesde(todos[0].fecha) : 0 };
    },
    [],
    { n: 0, dias: 0 },
  );
  const pendientes = cola.n;
  const pedidosHoy = useLiveQuery(
    async () =>
      (await db.pedidos.where('estado').equals('pendiente').toArray()).filter((p) => p.fechaEntrega <= hoyEs).length,
    [hoyEs],
    0,
  );
  /**
   * With `apartarPedidos` on (2026-10-08): the orders still to deliver that the shop's stock no
   * longer covers, after a sale the owner let through or one from a till that had not heard of
   * them. Said here before the day they go out, while there is time to buy.
   */
  const pedidosSinMercaderia = useLiveQuery(
    async () => {
      if (!(await readSettings()).apartarPedidos) return 0;
      const [pedidos, unidades, existencias] = await Promise.all([
        db.pedidos.where('estado').equals('pendiente').toArray(),
        db.unidades.toArray(),
        db.existencias.toArray(),
      ]);
      return pedidosCortos({ pedidos, factorDe: factoresDe(unidades), hayDe: hayEnLaTienda(existencias) }).size;
    },
    [],
    0,
  );
  const acabandose = useLiveQuery(
    async () => (puedeComprar ? contarAcabandose() : 0),
    [puedeComprar],
    0,
  );
  /**
   * Products sold past what the books held, off the trucks (a route squares its own at the
   * settlement). Each is a count waiting to be done: until then the stock report is wrong and a
   * real loss can hide in it.
   */
  const negativos = useLiveQuery(
    async () =>
      puedeAjustar
        ? (await db.existencias.toArray()).filter((e) => e.cantidad < 0 && !esCamion(e.ubicacion)).length
        : 0,
    [puedeAjustar],
    0,
  );
  const porVencer = useLiveQuery(
    async () => tablero(await db.lotes.toArray(), (await readSettings()).vencimientoAlertaDias).length,
    [],
    0,
  );
  /**
   * The fixed expenses whose day came and are not recorded yet (the owner, 2026-10-07): the rent
   * said here before it is late. The ones still to come wait in «Por pagar».
   */
  const fijosQueTocan = useLiveQuery(
    async () => (puedeGastos ? (await leerPendientes(hoyEs)).filter((o) => o.estado !== 'proximo') : []),
    [puedeGastos, hoyEs],
    [],
  );
  /**
   * The last day the owner's week was gone over to, and whether a till has ever closed
   * (`hayQueCuadrar`). The envelopes the tills leave wait for that going over: the home screen
   * no longer says how many there are, all week long (the owner, 2026-10-03).
   */
  const cuadre = useLiveQuery(
    async () => {
      if (!puedeRecibir) return null;
      const [semanas, cortes, sinEfecto] = await Promise.all([
        db.eventos.where('type').equals('semana.cuadrada').toArray(),
        db.sesiones.where('estado').equals('cerrada').count(),
        leerSinEfecto(),
      ]);
      const ultimo =
        semanas
          .filter((e) => tuvoEfecto(e, sinEfecto))
          .map((e) => (e.payload as { hasta: string }).hasta)
          .sort()
          .at(-1) ?? null;
      return { ultimo, hayCortes: cortes > 0 };
    },
    [puedeRecibir],
    null,
  );
  const ventasHoy = useLiveQuery(
    async () => {
      const vs = await db.ventas.where('fecha').equals(hoyEs).toArray();
      return vs.filter((v) => !v.anulada).reduce((a, v) => a + v.total, 0);
    },
    [hoyEs],
    0,
  );

  // Latch it the first time the shop can actually sell, and never take the screen again.
  useEffect(() => {
    if (!arranque.puedeVender || yaEstuvoListo) return;
    escribirBandera(CLAVE_LISTO, true);
    setYaEstuvoListo(true);
  }, [arranque.puedeVender, yaEstuvoListo]);

  // Dexie has not answered yet. A blank moment beats a screen that says the wrong thing: read too
  // soon, the big button said «Empezar mi turno» over a till that was open, then «Vender».
  if (arranque.cargando || caja === undefined || cajaDeOtro === undefined || (caja === null && empiezaElDia === undefined)) return null;

  const enArranque = !yaEstuvoListo && !arranque.puedeVender && arranque.siguiente !== null;

  /** Somebody who can finish the setup is walked through it. */
  if (enArranque && puedeArrancar && !saltado) {
    return (
      <Arranque
        estado={arranque}
        onSaltar={() => {
          escribirBandera(CLAVE_SALTADO, true);
          setSaltado(true);
        }}
      />
    );
  }
  /**
   * Somebody who cannot is told the truth on a shop with nothing in it yet, rather than handed a
   * till with nothing to sell. Only there: a product without a price is the till's to refuse and
   * «Precios»' to fix, and a cashier may price it. Every step but the name used to count, so one
   * product left unpriced put every cashier of a running shop here, unable to open anything (the
   * owner, 2026-10-13: «it's too strict»).
   */
  const sinProductos = !arranque.pasos.find((p) => p.clave === 'productos')!.hecho;
  if (sinProductos && !puedeArrancar) return <Esperando />;

  const rol = (sesion?.rol ?? 'cajero') as Role;
  const faltaPrecio = arranque.pasos.find((p) => p.clave === 'precios')!;
  /** The route this device's till belongs to: settling it is the next thing, whoever is signed in. */
  const enRuta = caja ? rutasAbiertas.find((r) => r.jornada.sesionId === caja.id)?.jornada : undefined;
  /**
   * The other devices' routes, to the owner, who settles them: there, on that phone, where the
   * cash and whoever had the route are. The goods and the cash of each are unaccounted for since.
   */
  const rutasDeOtros = puedeLiquidar ? rutasAbiertas.filter((r) => r.jornada.sesionId !== caja?.id) : [];
  const nombreCamion = (ubicacion: string) =>
    (config?.camiones ?? []).find((c) => `camion:${c.id}` === ubicacion)?.nombre ?? t('reparto.camion');
  const fechaLarga = (fecha: string) =>
    new Date(`${fecha}T12:00:00Z`).toLocaleDateString(localeActual(), { day: 'numeric', month: 'long', timeZone: ZONA_HORARIA });
  /**
   * This device's till from a day before, said under the big button to whoever has it: its corte
   * first. Somebody else's here is received instead, and the big button says so already. The other
   * devices' tills, to the owner, who receives what they hand over.
   */
  const cajaVieja = caja && !cajaDeOtro ? deOtroDia.find((x) => x.sesion.id === caja.id) : undefined;
  const otrasViejas = puedeRecibir ? deOtroDia.filter((x) => x.sesion.id !== caja?.id) : [];

  /** The one thing most likely to be next, given what the shop is actually doing right now. */
  const principal: Accion = enRuta
    ? { etiqueta: 'inicio.liquidarRuta', destino: '/caja/terminar', icono: 'liquidar' }
    : !caja && puedeCaja
      ? { etiqueta: empiezaElDia ? 'turno.empezarDia' : 'turno.empezarTurno', destino: '/caja/empezar', icono: 'caja' }
      : { etiqueta: 'inicio.vender', destino: '/pos', icono: 'vender' };

  /** What ends what the big button started: the till's corte. On a route's, the big button is it. */
  const cierre: (Accion & { ayuda: Clave }) | null =
    caja && !enRuta
      ? { etiqueta: 'turno.terminarTurno', ayuda: 'inicio.ayudaCerrarCaja', destino: '/caja/terminar', icono: 'caja' }
      : null;
  const atajos = atajosPara(rol, puede);
  /** «Bloquear/Cambiar de usuario» in its two parts: the first is what a phone has room for. */
  const [primeraDelBloqueo, ...demasDelBloqueo] = t('inicio.bloquear').split('/');
  const restoDelBloqueo = demasDelBloqueo.join('/');
  /** The owner's going over the week: a week after the last one, or once there is a week to go over. */
  const cuadrar = cuadre !== null && hayQueCuadrar({ ultimo: cuadre.ultimo, hoy: hoyEs, hayCortes: cuadre.hayCortes });
  const diasSinCuadrar = cuadre?.ultimo ? Math.round((Date.parse(hoyEs) - Date.parse(cuadre.ultimo)) / 86_400_000) : null;

  /** The fixed expenses due, each named once: «Alquiler del local (2 meses), Internet Claro». */
  const fijosVencidos = fijosQueTocan.some((o) => o.estado === 'vencido');
  const nombresFijos = porFijo(fijosQueTocan).map(({ fijo, meses }) =>
    meses > 1 ? t('inicio.tocaPagarMeses', { descripcion: fijo.descripcion, n: meses }) : fijo.descripcion,
  );

  return (
    <div className="escalonar mx-auto flex w-full max-w-2xl flex-col gap-5 p-5 pb-16">
      {/*
        * Wraps rather than truncates. The shop's name, a language toggle and a way into the
        * settings do not fit across 360px, and the name is the part nobody wants shortened —
        * so on a narrow phone the two controls drop to their own line instead. In Spanish they
        * do at any width since «Bloquear/Cambiar de usuario» (2026-10-16), the name over them.
        */}
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1 basis-40">
          <h1 className="truncate text-xl font-bold">{config?.negocioNombre ?? 'Abastece Ya'}</h1>
          <p className="truncate text-sm text-slate-400">
            {sesion ? `${sesion.nombre} · ${etiquetaRol(rol)}` : t('inicio.sinSesion')}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Idioma tamano="breve" />
          <Link to="/ajustes" className="btn-secundario h-11 min-h-0 gap-1.5 px-4 text-sm">
            <Ico icono="ajustes" className="size-4" />
            {t('inicio.ajustes')}
          </Link>
          {/*
            For stepping away from the counter: the PIN pad at once, instead of in fifteen minutes,
            which is also where somebody else signs in (the owner, 2026-10-16: «Change the label on
            the Lock button to "Lock/Switch User"»). The whole of it fits beside the other two from
            540px (measured: «Bloquear/Cambiar de usuario» needs 504 of the row); under that it says
            its first part, «Bloquear», as it did; under 390px the padlock alone. Its name is the
            whole label at any width, for screen readers: the parts out of sight left half of it
            out, or put a space in it.
          */}
          <button
            type="button"
            className="btn-secundario h-11 min-h-0 min-w-11 gap-1.5 px-3 text-sm"
            onClick={bloquear}
            aria-label={t('inicio.bloquear')}
            data-inicio-bloquear
          >
            <Ico icono="bloquear" className="size-4" />
            <span className="max-[389px]:hidden">
              {primeraDelBloqueo}
              {restoDelBloqueo && <span className="max-[540px]:hidden">/{restoDelBloqueo}</span>}
            </span>
          </button>
        </div>
      </header>

      {/* Skipping setup is allowed, forgetting about it is not. */}
      {enArranque && puedeArrancar && saltado && (
        <button
          type="button"
          className="tarjeta-accion flex items-center gap-3 p-3.5 text-left ring-marca-500/25"
          onClick={() => {
            escribirBandera(CLAVE_SALTADO, false);
            setSaltado(false);
          }}
        >
          <Chip icono="negocio" tinte="marca" />
          <span className="min-w-0 flex-1 text-sm font-medium text-marca-300">
            {t('arranque.pendiente')}
          </span>
          <span className="pastilla shrink-0 bg-marca-500/15 text-marca-300">
            {t('arranque.paso', { n: arranque.hechos + 1, total: arranque.total })}
          </span>
        </button>
      )}

      {cajaDeOtro ? (
        <Link
          to="/caja/recibir"
          className="hero vt-principal"
          data-guia="inicio-principal"
          onClick={() => marcarOrigen('principal')}
        >
          <span className="pointer-events-none absolute inset-x-0 -top-16 h-32 bg-white/10 blur-2xl" />
          <Ico icono="contar" className="relative size-8 shrink-0" />
          <span className="relative">{t('entrega.recibirDe', { nombre: cajaDeOtro.nombre })}</span>
        </Link>
      ) : (
        <Link
          to={principal.destino}
          className="hero vt-principal"
          data-guia="inicio-principal"
          onClick={() => marcarOrigen('principal')}
        >
          {/* A soft highlight across the top, so the one big surface is not a flat slab. */}
          <span className="pointer-events-none absolute inset-x-0 -top-16 h-32 bg-white/10 blur-2xl" />
          <Ico icono={principal.icono} className="relative size-8 shrink-0" />
          <span className="relative">{t(principal.etiqueta)}</span>
        </Link>
      )}
      {cajaDeOtro && (
        <p className="-mt-2 text-center text-xs text-amber-300">{t('entrega.aviso', { nombre: cajaDeOtro.nombre })}</p>
      )}

      {!caja && principal.destino !== '/caja/empezar' && (
        <p className="-mt-2 text-center text-xs text-amber-300">{t('inicio.cajaCerrada')}</p>
      )}
      {cajaVieja && (
        <p className="-mt-2 text-center text-xs text-amber-300" data-inicio-caja-vieja>
          {t('inicio.cajaDeOtroDia', { fecha: fechaLarga(cajaVieja.desde) })}
        </p>
      )}
      {enRuta && (
        <p className="-mt-2 text-center text-xs text-amber-300" data-inicio-ruta>
          {t('inicio.enRuta', { camion: nombreCamion(enRuta.camion), fecha: fechaLarga(enRuta.fecha) })}
        </p>
      )}

      {cierre && (
        <ConGlobo id={`${globo}-cierre`} texto={t(cierre.ayuda)} lado="izquierda">
          <Link to={cierre.destino} aria-describedby={`${globo}-cierre`} className="barra-cierre" data-inicio-cierre>
            <span className="chip bg-white/15 text-white ring-white/25">
              <Ico icono={cierre.icono} className="size-5" />
            </span>
            <span className="min-w-0 flex-1">{t(cierre.etiqueta)}</span>
            <Ico icono="adelante" className="size-5 shrink-0 text-white/70" />
          </Link>
        </ConGlobo>
      )}

      {cuadrar && (
        <Link to="/dinero/semana" className="tarjeta-accion flex items-center gap-3 p-3.5 ring-emerald-500/25" data-inicio-cuadrar>
          <Chip icono="ganancia" tinte="esmeralda" />
          <span className="min-w-0 flex-1">
            <span className="block font-medium">{t('cuadrar.titulo')}</span>
            <span className="block text-xs text-slate-400">
              {diasSinCuadrar === null ? t('cuadrar.inicioPrimera') : t('cuadrar.inicioHaceDias', { n: diasSinCuadrar })}
            </span>
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-slate-600" />
        </Link>
      )}

      {/*
        * Every row as tall as the tallest, so that a row whose labels all fit on one line does not
        * come out shorter than the rest. Under 360 px the tiles are tighter and the words a size
        * smaller, which is what «Contabilidad» needs to fit a third of a 320 px phone. Nothing is
        * hyphenated: a phone that can would make «Llegó mer-cadería» out of two words that break
        * cleanly between them. A word wider than its tile all the same, in some phone's wider
        * font, breaks rather than spills.
        */}
      {atajos.length > 0 && (
        <nav
          aria-label={t('inicio.atajos')}
          data-guia="inicio-atajos"
          className={`grid auto-rows-fr gap-2.5 max-[359px]:gap-2 ${columnasPara(atajos.length)}`}
        >
          {atajos.map((a, i) => (
            <ConGlobo
              key={a.destino}
              id={`${globo}-${i}`}
              texto={t(a.ayuda)}
              lado={ladoDelGlobo(i, atajos.length)}
            >
              <Link
                to={a.destino}
                search={a.abreFormulario ? { nuevo: true } : {}}
                aria-describedby={`${globo}-${i}`}
                className="tarjeta-accion flex h-full flex-col items-center gap-1 px-2 py-2 text-center max-[359px]:px-1"
              >
                <Chip icono={a.icono} tinte={tinteDe(a)} mas={a.mas} />
                <span className="text-[13px] leading-[1.15] font-medium [overflow-wrap:anywhere] max-[359px]:text-xs">
                  <PartidoEnLaBarra texto={t(a.etiqueta)} />
                </span>
              </Link>
            </ConGlobo>
          ))}
        </nav>
      )}

      <EstadoSync />

      {/* Wrapped so the tutorial can point at "the alerts" as one thing. */}
      <div className="flex flex-col gap-3 empty:hidden" data-guia="inicio-avisos">
      {porVencer > 0 && puedeConfigurar && (
        <Link
          to="/vencimientos"
          className="tarjeta-accion flex items-center gap-3 p-3.5 ring-rose-500/25"
        >
          <Chip icono="vencimientos" tinte="rosa" />
          <span className="min-w-0 flex-1 text-sm font-medium text-rose-200">
            {t('inicio.lotesPorVencer', { n: porVencer })}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-rose-300/60" />
        </Link>
      )}

      {fijosQueTocan.length > 0 && (
        <Link
          to="/gastos"
          search={{ vista: 'porPagar' }}
          className={`tarjeta-accion flex items-center gap-3 p-3.5 ${fijosVencidos ? 'ring-rose-500/25' : 'ring-amber-500/25'}`}
          data-inicio-fijos
        >
          <Chip icono="gastos" tinte={fijosVencidos ? 'rosa' : 'ambar'} />
          <span className={`min-w-0 flex-1 text-sm font-medium ${fijosVencidos ? 'text-rose-200' : 'text-amber-200'}`}>
            {t('inicio.tocaPagar', {
              lista:
                nombresFijos.length > 3
                  ? t('inicio.tocaPagarMas', { lista: nombresFijos.slice(0, 3).join(', '), n: nombresFijos.length - 3 })
                  : nombresFijos.join(', '),
            })}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-slate-400/60" />
        </Link>
      )}

      {negativos > 0 && (
        <Link
          to="/inventario"
          search={{ negativos: true }}
          className="tarjeta-accion flex items-center gap-3 p-3.5 ring-amber-500/25"
        >
          <Chip icono="inventario" tinte="ambar" />
          <span className="min-w-0 flex-1 text-sm font-medium text-amber-200">
            {t('inicio.negativos', { n: negativos })}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-amber-300/60" />
        </Link>
      )}

      {acabandose > 0 && puedeComprar && (
        <Link
          to="/reponer"
          className="tarjeta-accion flex items-center gap-3 p-3.5 ring-amber-500/25"
        >
          <Chip icono="reponer" tinte="ambar" />
          <span className="min-w-0 flex-1 text-sm font-medium text-amber-200">
            {t('inicio.seEstanAcabando', { n: acabandose })}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-amber-300/60" />
        </Link>
      )}

      {pedidosSinMercaderia > 0 && puedePedidos && (
        <Link to="/pedidos" className="tarjeta-accion flex items-center gap-3 p-3.5 ring-rose-500/25" data-inicio-pedidos-cortos>
          <Chip icono="pedidos" tinte="rosa" />
          <span className="min-w-0 flex-1 text-sm font-medium text-rose-200">
            {t('inicio.pedidosCortos', { n: pedidosSinMercaderia })}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-rose-300/60" />
        </Link>
      )}

      {/* Whoever can open «Pedidos» may be the one taking them out: drivers, cashiers, the owner. */}
      {pedidosHoy > 0 && puedePedidos && (
        <Link
          to="/pedidos"
          className="tarjeta-accion flex items-center gap-3 p-3.5 ring-marca-500/25"
        >
          <Chip icono="pedidos" tinte="marca" />
          <span className="min-w-0 flex-1 text-sm font-medium text-marca-200">
            {t('inicio.pedidosHoy', { n: pedidosHoy })}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-marca-300/60" />
        </Link>
      )}

      {otrasViejas.map((x) => (
        <div key={x.sesion.id} className="tarjeta flex items-center gap-3 p-3.5 ring-rose-500/25" data-inicio-caja-abierta>
          <Chip icono="caja" tinte="rosa" />
          <span className="min-w-0 flex-1 text-sm font-medium text-rose-200">
            {t('inicio.cajaAbiertaDesde', {
              nombre: usuariosTodos.find((u) => u.id === x.usuarioId)?.nombre ?? t('ventas.alguien'),
              equipo: x.equipo,
              fecha: fechaLarga(x.desde),
            })}
          </span>
        </div>
      ))}

      {rutasDeOtros.map(({ jornada: j, equipo }) => (
        <div key={j.id} className="tarjeta flex items-center gap-3 p-3.5 ring-rose-500/25" data-inicio-ruta-abierta>
          <Chip icono="reparto" tinte="rosa" />
          <span className="min-w-0 flex-1 text-sm font-medium text-rose-200">
            {t('inicio.rutaSinLiquidar', { camion: nombreCamion(j.camion), fecha: fechaLarga(j.fecha), equipo })}
          </span>
        </div>
      ))}

      {preciosBajos > 0 && (
        <button
          type="button"
          onClick={() => setViendoPrecios(true)}
          className="tarjeta-accion flex items-center gap-3 p-3.5 text-left ring-rose-500/25"
        >
          <Chip icono="precios" tinte="rosa" />
          <span className="min-w-0 flex-1 text-sm font-medium text-rose-200">
            {t('inicio.preciosBajoMargen', { n: preciosBajos })}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-rose-300/60" />
        </button>
      )}
      {viendoPrecios && <PreciosBajoMargen onCerrar={() => setViendoPrecios(false)} />}

      {faltantesCaja.length > 0 && (
        <Link to="/control" className="tarjeta-accion flex items-center gap-3 p-3.5 ring-rose-500/25">
          <Chip icono="caja" tinte="rosa" />
          <span className="min-w-0 flex-1 text-sm font-medium text-rose-200">
            {t('inicio.faltantesCaja', {
              n: faltantesCaja.length,
              monto: formatMoney(faltantesCaja[0]!.monto as never),
              nombre: usuariosTodos.find((u) => u.id === faltantesCaja[0]!.usuarioId)?.nombre ?? '',
            })}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-rose-300/60" />
        </Link>
      )}

      {(control?.sinAutorizar.length ?? 0) > 0 && (
        <Link to="/control" className="tarjeta-accion flex items-center gap-3 p-3.5 ring-rose-500/25">
          <Chip icono="aviso" tinte="rosa" />
          <span className="min-w-0 flex-1 text-sm font-medium text-rose-200">
            {t('inicio.sinAutorizar', { n: control!.sinAutorizar.length })}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-rose-300/60" />
        </Link>
      )}

      {pinesDelDueno >= SEGUIDOS_PARA_AVISAR && (
        <Link
          to="/bitacora"
          search={{ filtro: 'sistema' }}
          className="tarjeta-accion flex items-center gap-3 p-3.5 ring-rose-500/25"
        >
          <Chip icono="bloquear" tinte="rosa" />
          <span className="min-w-0 flex-1 text-sm font-medium text-rose-200">
            {t('inicio.pinesDelDueno', { n: pinesDelDueno })}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-rose-300/60" />
        </Link>
      )}

      {(delServidor?.rechazos ?? 0) > 0 && (
        <Link
          to="/bitacora"
          search={{ vista: 'rechazos' }}
          className="tarjeta-accion flex items-center gap-3 p-3.5 ring-rose-500/25"
        >
          <Chip icono="aviso" tinte="rosa" />
          <span className="min-w-0 flex-1 text-sm font-medium text-rose-200">
            {t('inicio.rechazosSinRevisar', { n: delServidor!.rechazos })}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-rose-300/60" />
        </Link>
      )}

      {(delServidor?.callados ?? 0) > 0 && (
        <Link
          to="/ajustes/personal"
          className="tarjeta-accion flex items-center gap-3 p-3.5 ring-amber-500/25"
        >
          <Chip icono="telefono" tinte="ambar" />
          <span className="min-w-0 flex-1 text-sm font-medium text-amber-200">
            {t('inicio.equiposCallados', { n: delServidor!.callados, dias: Math.round(horasDeAviso(config?.equipoCalladoHoras ?? 24) / 24) })}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-amber-300/60" />
        </Link>
      )}


      {/* Two days unconfirmed is a transfer that is probably not coming: said louder. */}
      {pendientes > 0 && puedeConfirmar && (
        <Link
          to="/transferencias"
          className={`tarjeta-accion flex items-center gap-3 p-3.5 ${cola.dias >= 2 ? 'ring-rose-500/25' : 'ring-amber-500/25'}`}
        >
          <Chip icono="transferencias" tinte={cola.dias >= 2 ? 'rosa' : 'ambar'} />
          <span className={`min-w-0 flex-1 text-sm font-medium ${cola.dias >= 2 ? 'text-rose-200' : 'text-amber-200'}`}>
            {cola.dias >= 2
              ? t('inicio.transferenciasViejas', { n: pendientes, dias: cola.dias })
              : t('inicio.transferenciasPendientes', { n: pendientes })}
          </span>
          <Ico icono="adelante" className={`size-5 shrink-0 ${cola.dias >= 2 ? 'text-rose-300/60' : 'text-amber-300/60'}`} />
        </Link>
      )}

      {/*
        * After setup, an unpriced product is not a shop that needs configuring again — it is
        * one product somebody has to price before it can be sold. This says so without taking
        * the screen, and the POS still refuses it at the counter if it gets that far. To whoever
        * may price it (`editar_precios`, the owner's), unless the setup is what walks them there.
        */}
      {!(enArranque && puedeArrancar) && puedePrecios && !faltaPrecio.hecho && faltaPrecio.faltan > 0 && (
        <Link
          to="/precios"
          className="tarjeta-accion flex items-center gap-3 p-3.5 ring-amber-500/25"
        >
          <Chip icono="precios" tinte="ambar" />
          <span className="min-w-0 flex-1 text-sm font-medium text-amber-200">
            {t('inicio.sinPrecio', { n: faltaPrecio.faltan })}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-amber-300/60" />
        </Link>
      )}

      {/* Not blocking anything yet, but the day they are needed is a bad day to find out. */}
      {puedeConfigurar &&
        arranque.puedeVender &&
        arranque.luego.map((c) => (
          <Link
            key={c}
            to="/proveedores"
            className="tarjeta-accion flex items-center gap-3 p-3.5 text-sm text-slate-400"
          >
            <Chip icono="proveedores" tinte="neutro" />
            <span className="min-w-0 flex-1">{t('inicio.faltaProveedor')}</span>
            <Ico icono="adelante" className="size-5 shrink-0 text-slate-600" />
          </Link>
        ))}
      </div>

      {/*
        A figure invites the question of what is in it, so it opens the sales it adds up. The
        chevron sits by the label, not at the end of the row: there it took the room the figure
        needs, and C$ 1,484.64 broke over two lines on a 360px phone.
      */}
      <Link
        to="/ventas"
        search={{ hoy: true }}
        className="tarjeta-accion vt-vendido flex items-center gap-4"
        onClick={() => marcarOrigen('vendido')}
      >
        <Chip icono="ganancia" tinte="esmeralda" />
        <div className="@container min-w-0 flex-1">
          <p className="flex items-center gap-1 text-sm text-slate-400">
            {t('inicio.vendidoHoy')}
            <Ico icono="adelante" className="size-4 text-slate-500" />
          </p>
          {/*
            One line, however good the day. A figure broken over two lines («C$ 12,345.» and
            «67») reads as two numbers; past C$ 10,000 it broke on a 360 px phone, and at 320
            past C$ 1,000. So the figure is as big as it always was while it fits, and smaller
            as it grows: by its length, against the width of its own column (`cqw`).
          */}
          <p
            className="tabular mt-0.5 font-bold whitespace-nowrap"
            style={{ fontSize: `min(1.875rem, ${(100 / (0.64 * formatMoney(ventasHoy as never).length)).toFixed(1)}cqw)` }}
          >
            {formatMoney(ventasHoy as never)}
          </p>
        </div>
      </Link>

      <Link to="/mas" className="tarjeta-accion flex items-center gap-4 p-3.5" data-guia="inicio-mas">
        <Chip icono="ajustes" tinte="neutro" />
        <span className="min-w-0 flex-1 font-medium">{t('inicio.mas')}</span>
        <Ico icono="adelante" className="size-5 shrink-0 text-slate-600" />
      </Link>
    </div>
  );
}

/**
 * A tile's name in two parts, «Contabilidad/Planilla», breaks before its slash when the tile is
 * narrow, as an address is broken: «Contabilidad» over «/Planilla», never inside either word.
 * After it, «Contabilidad/» was a letter wider than the tile at 360 px.
 */
function PartidoEnLaBarra({ texto }: { texto: string }) {
  return texto.split('/').map((parte, i) => (
    <Fragment key={i}>
      {i > 0 && <wbr />}
      {i > 0 ? `/${parte}` : parte}
    </Fragment>
  ));
}
